import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
  AppSelectWithKeys,
} from 'shared/components/AppComponents';

import { GuardrailsService } from 'services/GuardrailsService';

interface ScopeOption {
  id: string;
  title: string;
  description: string;
}

interface GuardrailGroup {
  _id: string;
  name: string;
}

interface GuardrailPolicyData {
  _id: string;
  policyName: string;
  description: string;
  responseActions: string;
  enabled: boolean;
  type: string;
  monitoring: string;
  guardrailsGroupId: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  organizationId: string | null;
  isActive: boolean;
  __v: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => void;
  editData?: GuardrailPolicyData | null;
}

const scopeOptions: ScopeOption[] = [
  {
    id: 'user_input',
    title: 'User Input',
    description: 'Flag when a user sends something matching this policy',
  },
  {
    id: 'agent_response',
    title: 'Agent Response',
    description:
      'Flag when the AI agent responds with something matching this policy',
  },
  {
    id: 'full_conversation',
    title: 'Full Conversation',
    description:
      'Analyze the entire session thread for patterns that span multiple turns',
  },
];

const CreateCustomGuardrailModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  editData,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [responseActions, setResponseActions] = useState('');
  const [governanceGroup, setGovernanceGroup] = useState('');
  const [newGroupName, setNewGroupName] = useState('');

  const [guardrailGroups, setGuardrailGroups] = useState<GuardrailGroup[]>([]);

  const [selectedScopes, setSelectedScopes] = useState<string>('user_input');

  // EDIT BIND
  useEffect(() => {
    if (editData) {
      setName(editData.policyName || '');
      setDescription(editData.description || '');
      setResponseActions(editData.responseActions || '');
      setGovernanceGroup(editData.guardrailsGroupId || '');
      setSelectedScopes(editData.monitoring || 'user_input');
    } else {
      setName('');
      setDescription('');
      setResponseActions('');
      setGovernanceGroup('');
      setSelectedScopes('user_input');
      setNewGroupName('');
    }
  }, [editData, open]);

  const handleScopeToggle = (scopeId: string): void => {
    setSelectedScopes((prev) => {
      const currentScopes = prev ? prev.split(',') : [];

      if (currentScopes.includes(scopeId)) {
        // Remove scope
        const filtered = currentScopes.filter((item) => item !== scopeId);
        return filtered.join(',');
      }

      // Add scope
      return [...currentScopes, scopeId].join(',');
    });
  };

  const isScopeSelected = (scopeId: string): boolean => {
    const scopes = selectedScopes ? selectedScopes.split(',') : [];
    return scopes.includes(scopeId);
  };

  // CREATE / UPDATE POLICY
  const handleSubmit = async (): Promise<void> => {
    setIsSubmitting(true);

    try {
      const payload = {
        policyName: name,
        monitoring: selectedScopes,
        description,
        responseActions,
        guardrailsGroupId: governanceGroup,
        newGroupName,
      };

      await onSubmit(payload);
      onClose();
    } catch (error) {
      console.log('Error in form submission:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // LOAD GROUPS
  const loadGuardRailGroups = (): void => {
    setIsLoading(true);

    GuardrailsService.getAllGuardRailGroups()
      .then((response) => {
        const groups = response.data.data || [];

        const updatedGroups = [
          {
            _id: '',
            name: 'Select a group',
          },
          ...groups,
          {
            _id: 'new_group',
            name: '+ New Group',
          },
        ];

        setGuardrailGroups(updatedGroups);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const hasSelectedScopes = (): boolean => {
    const scopes = selectedScopes ? selectedScopes.split(',') : [];
    return scopes.length > 0 && scopes[0] !== '';
  };

  useEffect(() => {
    if (open) {
      loadGuardRailGroups();
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      className="custom-guardrail-modal"
    >
      <Box className="modal-wrapper">
        {/* HEADER */}
        <Box className="modal-header">
          <Typography className="modal-title">
            {editData ? 'Update Custom Guardrail' : 'Create Custom Guardrail'}
          </Typography>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* BODY */}
        <Box className="modal-body">
          {/* NAME */}
          <Box className="form-group">
            <Typography className="field-label">
              Name
              <Typography component="span" className="required">
                *
              </Typography>
            </Typography>

            <TextField
              fullWidth
              placeholder="Policy display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>

          {/* MONITOR */}
          <Box className="form-group">
            <Typography className="field-label">
              What should this monitor?
              <Typography component="span" className="required">
                *
              </Typography>
            </Typography>

            <Typography className="helper-text">
              Select one or more scopes.
            </Typography>

            <Box className="scope-list">
              {scopeOptions.map((scope) => (
                <Box
                  key={scope.id}
                  className={`scope-card ${
                    selectedScopes.includes(scope.id) ? 'active' : ''
                  }`}
                  onClick={() => handleScopeToggle(scope.id)}
                >
                  <FormControlLabel
                    control={<Checkbox checked={isScopeSelected(scope.id)} />}
                    label=""
                  />

                  <Box className="scope-content">
                    <Typography className="scope-title">
                      {scope.title}
                    </Typography>

                    <Typography className="scope-description">
                      {scope.description}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {!hasSelectedScopes() && (
              <Typography
                sx={{
                  color: '#d32f2f',
                  mt: 2,
                }}
              >
                Select at least one scope.
              </Typography>
            )}
          </Box>

          {/* DESCRIPTION */}
          <Box className="form-group">
            <Typography className="field-label">
              Description
              <Typography component="span" className="required">
                *
              </Typography>
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Write your rule in plain language"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Box>

          {/* GOVERNANCE GROUP */}
          <Box className="form-group">
            <Typography className="field-label">
              Governance Group
              <Typography component="span" className="required">
                *
              </Typography>
            </Typography>

            {isLoading ? (
              <CircularProgress size={24} />
            ) : (
              <AppSelectWithKeys
                placeholder="Select group"
                value={governanceGroup}
                onChange={(e: any) => {
                  setGovernanceGroup(e.target.value);
                }}
                items={guardrailGroups}
                titleKey="name"
                valueKey="_id"
              />
            )}
          </Box>

          {/* NEW GROUP */}
          {governanceGroup === 'new_group' && (
            <Box className="form-group">
              <TextField
                fullWidth
                placeholder="New group name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />
            </Box>
          )}

          {/* RESPONSE ACTIONS */}
          <Box className="form-group">
            <Typography className="field-label">
              Response Actions
              <Typography component="span" className="required">
                *
              </Typography>
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Describe the actions to take"
              value={responseActions}
              onChange={(e) => setResponseActions(e.target.value)}
            />
          </Box>
        </Box>

        {/* FOOTER */}
        <Box className="modal-footer">
          <AppPrimaryButtonOutlined title="Cancel" onClick={onClose} />

          <AppPrimaryButton
            title={editData ? 'Update' : 'Create'}
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !name.trim() ||
              !description.trim() ||
              !responseActions.trim() ||
              !governanceGroup ||
              (governanceGroup === 'new_group' && !newGroupName.trim()) ||
              !hasSelectedScopes()
            }
          />
        </Box>
      </Box>
    </Dialog>
  );
};

export default CreateCustomGuardrailModal;
